import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import api from '../api/client';
import { useAuth } from './AuthContext';
import { pushContinue as pushContinueApi } from '../api/services';

const AppContext = createContext(null);

const DEFAULT_PROFILES = [
  { id: 'alex', name: 'Alex', avatar: 'https://i.pravatar.cc/300?img=12', color: '#E50914', is_kids: false },
  { id: 'mia', name: 'Mia', avatar: 'https://i.pravatar.cc/300?img=47', color: '#7C3AED', is_kids: false },
  { id: 'sam', name: 'Sam', avatar: 'https://i.pravatar.cc/300?img=33', color: '#06B6D4', is_kids: false },
  { id: 'kids', name: 'Kids', avatar: 'https://i.pravatar.cc/300?img=56', color: '#F59E0B', is_kids: true },
];

const toItem = (m) => ({
  id: m.tmdb_id,
  tmdb_id: m.tmdb_id,
  media_type: m.media_type,
  title: m.title,
  name: m.title,
  poster_path: m.poster_path,
  backdrop_path: m.backdrop_path,
  vote_average: m.vote_average,
  release_date: m.release_date,
  _key: `${m.media_type}-${m.tmdb_id}`,
});

export const AppProvider = ({ children }) => {
  const { user } = useAuth();
  const [profile, setProfile] = useState(() => {
    try { return JSON.parse(localStorage.getItem('rk_profile')); } catch { return null; }
  });
  const [myList, setMyList] = useState([]);
  const [loadingList, setLoadingList] = useState(false);

  useEffect(() => {
    if (profile) localStorage.setItem('rk_profile', JSON.stringify(profile));
    if (profile && user) {
      api.put('/profile/pref', {
        profile_id: profile.id,
        name: profile.name,
        avatar: profile.avatar,
        color: profile.color,
        is_kids: !!profile.is_kids,
      }).catch(() => {});
    }
  }, [profile, user]);

  useEffect(() => {
    if (!user) {
      setProfile(null);
      setMyList([]);
      localStorage.removeItem('rk_profile');
    }
  }, [user]);

  useEffect(() => {
    if (!user) return;
    api.get('/profile/pref').then(({ data }) => {
      if (data && data.profile_id) {
        setProfile({
          id: data.profile_id, name: data.name, avatar: data.avatar,
          color: data.color, is_kids: !!data.is_kids,
        });
      }
    }).catch(() => {});
  }, [user]);

  useEffect(() => {
    if (!user) return;
    setLoadingList(true);
    api.get('/mylist')
      .then(({ data }) => setMyList((data || []).map(toItem)))
      .catch(() => setMyList([]))
      .finally(() => setLoadingList(false));
  }, [user]);

  const toggleMyList = async (item) => {
    if (!item) return;
    const media_type = item.media_type || (item.first_air_date ? 'tv' : 'movie');
    const tmdb_id = item.id || item.tmdb_id;
    const key = `${media_type}-${tmdb_id}`;
    const exists = myList.find((p) => p._key === key);
    if (exists) {
      setMyList((prev) => prev.filter((p) => p._key !== key));
    } else {
      setMyList((prev) => [{ ...item, tmdb_id, media_type, title: item.title || item.name, _key: key }, ...prev]);
    }
    if (!user) return;
    try {
      if (exists) {
        await api.delete(`/mylist/${media_type}/${tmdb_id}`);
      } else {
        await api.post('/mylist', {
          tmdb_id, media_type,
          title: item.title || item.name,
          poster_path: item.poster_path || null,
          backdrop_path: item.backdrop_path || null,
          vote_average: item.vote_average || 0,
          release_date: item.release_date || item.first_air_date || '',
        });
      }
    } catch {
      try {
        const { data } = await api.get('/mylist');
        setMyList((data || []).map(toItem));
      } catch {}
    }
  };

  const isInList = (item) => {
    if (!item) return false;
    const media_type = item.media_type || (item.first_air_date ? 'tv' : 'movie');
    const tmdb_id = item.id || item.tmdb_id;
    return !!myList.find((p) => p._key === `${media_type}-${tmdb_id}`);
  };

  const trackOpen = (item) => {
    if (!user || !item) return;
    const media_type = item.media_type || (item.first_air_date ? 'tv' : 'movie');
    pushContinueApi({
      tmdb_id: item.id || item.tmdb_id,
      media_type,
      title: item.title || item.name,
      poster_path: item.poster_path || null,
      backdrop_path: item.backdrop_path || null,
    }).catch(() => {});
  };

  const value = useMemo(
    () => ({ profile, setProfile, profiles: DEFAULT_PROFILES, myList, loadingList, toggleMyList, isInList, trackOpen }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [profile, myList, user, loadingList]
  );

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
};

export const useApp = () => useContext(AppContext);
